import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AlertCircle, Check, Save, Trash2 } from 'lucide-react';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import { useState } from 'react';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { encryptString, fetchEntitlements } from '@/lib/crypto';
import InlineTrivyImageList from './InlineTrivyImageList';

interface TrivyIntegrationProps {
  appId: string | undefined;
  projectTokens: any[];
  savedImages?: string[];
  pageLoading?: boolean;
  onTokenChange: () => Promise<void>;
  onSaveImages?: (images: string[]) => Promise<void>;
  onSnackbar: (message: string, severity: 'success' | 'error') => void;
}

export default function TrivyIntegration({
  appId,
  projectTokens,
  savedImages = [],
  pageLoading = false,
  onTokenChange,
  onSaveImages,
  onSnackbar,
}: TrivyIntegrationProps) {
  const [registryUrl, setRegistryUrl] = useState('');
  const [project, setProject] = useState('');
  const [username, setUsername] = useState('');
  const [robotSecret, setRobotSecret] = useState('');
  const [validation, setValidation] = useState<{ loading: boolean; saving: boolean; result: { ok: boolean; message: string } | null }>({ loading: false, saving: false, result: null });

  const trivyToken = projectTokens.find((t: any) => t.platform === 'Harbor' || t.platform === 'Trivy');

  const saveCredentials = async () => {
    setValidation((v) => ({ ...v, saving: true }));
    try {
      const { publicKey } = await fetchEntitlements();
      const configObj = {
        registryUrl: registryUrl.trim(),
        project: project.trim(),
        username: username.trim(),
        robotSecret,
        platform: 'Harbor',
      };
      const encryptedConfig = encryptString(JSON.stringify(configObj), publicKey);

      await sentinelNetwork.post(`/config/project/${appId}/tokens`, {
        platform: 'Harbor',
        encryptedConfig,
        url: registryUrl.trim(),
      });

      setValidation({ loading: false, saving: false, result: { ok: true, message: 'Saved' } });
      onSnackbar('Trivy / Container Registry connected', 'success');
      await onTokenChange();
      setRegistryUrl('');
      setProject('');
      setUsername('');
      setRobotSecret('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      setValidation({ loading: false, saving: false, result: { ok: false, message: `Save failed: ${msg}` } });
      onSnackbar('Failed to save Trivy credentials', 'error');
    }
  };

  const handleVerify = async () => {
    if (!registryUrl.trim() || !username.trim() || !robotSecret.trim()) {
      setValidation({ loading: false, saving: false, result: { ok: false, message: 'Registry URL, Username, and Robot Secret are required' } });
      return;
    }

    setValidation({ loading: true, saving: false, result: null });
    try {
      const res = await sentinelNetwork.post('/config/onboarding-secops/validate/trivy', {
        registryUrl: registryUrl.trim(),
        project: project.trim(),
        username: username.trim(),
        secret: robotSecret,
      });

      const result = res.data;
      if (!result.ok) {
        // Validation failed — show error but allow saving anyway (registry may be unreachable from cloud)
        setValidation({ loading: false, saving: false, result: { ok: false, message: result.message || 'Validation failed. Registry may be unreachable from cloud — you can still save.' } });
        return;
      }

      await saveCredentials();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      setValidation({ loading: false, saving: false, result: { ok: false, message: `Failed: ${msg}` } });
    }
  };

  const handleRemove = async () => {
    try {
      await sentinelNetwork.delete(`/config/project/${appId}/tokens`, {
        data: { platform: trivyToken?.platform || 'Harbor' },
      });
      onSnackbar('Trivy token removed', 'success');
      await onTokenChange();
    } catch {
      onSnackbar('Failed to remove Trivy token', 'error');
    }
  };

  const canSubmit = !validation.loading && !validation.saving && !!registryUrl.trim() && !!username.trim() && !!robotSecret.trim();

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <DeviceHubIcon fontSize="small" color="primary" />
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>
          Trivy / Container Registry
        </Typography>
        {trivyToken ? (
          <Chip icon={<Check size={12} />} label="Connected" size="small" color="success" sx={{ fontSize: 11, height: 22 }} />
        ) : (
          <Chip label="Not configured" size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
        )}
      </Box>

      {trivyToken ? (
        <Stack spacing={2}>
          {/* Registry info + Remove */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, flex: 1 }} noWrap>
              {trivyToken.url}
            </Typography>
            <Button size="small" color="error" variant="text" startIcon={<Trash2 size={14} />} onClick={handleRemove}>
              Remove
            </Button>
          </Stack>

          <Divider />

          {/* Image picker — gated on pageLoading so savedImages is populated before mount */}
          {!pageLoading && appId && onSaveImages && (
            <>
              {savedImages.length === 0 && (
                <Alert severity="warning" sx={{ mb: 1, py: 0.5, fontSize: 12 }}>
                  Select at least one image below to enable <strong>Fetch Report</strong>.
                </Alert>
              )}
              <InlineTrivyImageList
                appId={appId}
                savedImages={savedImages}
                onSave={onSaveImages}
              />
            </>
          )}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            Connect to a container registry for Trivy vulnerability scanning.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <TextField
              label="Registry URL"
              placeholder="https://registry.example.com"
              value={registryUrl}
              onChange={(e) => { setRegistryUrl(e.target.value); setValidation({ loading: false, saving: false, result: null }); }}
              size="small"
            />
            <TextField
              label="Project"
              placeholder="project-name"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <TextField
              label="Username"
              placeholder="robot$user"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setValidation({ loading: false, saving: false, result: null }); }}
              size="small"
            />
            <TextField
              label="Robot Secret"
              type="password"
              placeholder="Robot secret / token"
              value={robotSecret}
              onChange={(e) => { setRobotSecret(e.target.value); setValidation({ loading: false, saving: false, result: null }); }}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleVerify}
              disabled={!canSubmit}
              startIcon={validation.loading ? <CircularProgress size={14} /> : <WifiTetheringIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none' }}
            >
              {validation.loading ? 'Validating…' : 'Verify & Save'}
            </Button>

            {validation.result && !validation.result.ok && (
              <Button
                variant="contained"
                size="small"
                color="warning"
                onClick={saveCredentials}
                disabled={!canSubmit}
                startIcon={validation.saving ? <CircularProgress size={14} /> : <Save size={14} />}
                sx={{ textTransform: 'none' }}
              >
                {validation.saving ? 'Saving…' : 'Save Anyway'}
              </Button>
            )}

            {!validation.loading && !validation.saving && (
              validation.result?.ok
                ? <Chip icon={<Check size={14} />} label={validation.result.message} size="small" color="success" variant="outlined" />
                : validation.result
                  ? <Chip icon={<AlertCircle size={14} />} label={validation.result.message} size="small" color="warning" variant="outlined" sx={{ maxWidth: 320, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                  : null
            )}
          </Box>
        </Stack>
      )}

      <Alert severity="info" sx={{ mt: 2, py: 0.5 }} icon={<AlertCircle size={16} />}>
        Credentials are encrypted before storage and used for container image scanning.
      </Alert>
    </Paper>
  );
}
